/**
 * Module dependencies.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AreaTrendChart, CategoryBarChart, chartColors } from 'app/components/organisms/charts/charts';
import { Title } from 'app/components/atoms/title/title';
import { useTranslation } from 'react-i18next';
import { useMovementsOverview } from 'app/hooks/requests/use-movements-overview';
import moment from 'moment';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { appRoutes } from 'app/routes';
import { formatCurrency } from 'app/core/utils/formatter';
import { cn } from 'app/lib/utils';
import BigNumber from 'bignumber.js';
import { Percent, PiggyBank, TrendingDown, TrendingUp } from 'lucide-react';

/**
 * Muted "budget" bar colour (the "consumed" colour is per-section).
 */

const BUDGET_COLOR = '#94a3b8';

/**
 * `StatCard` component.
 */

function StatCard({
  icon,
  label,
  value,
  sub,
  tone
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  tone?: string;
}): JSX.Element {
  return (
    <div className={'rounded-xl border border-border bg-card p-5 shadow-sm'}>
      <div className={'flex items-center justify-between gap-3'}>
        <span className={'text-xs font-medium text-muted-foreground'}>{label}</span>

        <span
          className={
            'grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground [&_svg]:h-[18px] [&_svg]:w-[18px]'
          }
        >
          {icon}
        </span>
      </div>

      <div className={cn('mt-3 text-[26px] font-semibold leading-none tabular-nums text-foreground', tone)}>
        {value}
      </div>

      {!!sub && <div className={'mt-1.5 text-xs text-muted-foreground'}>{sub}</div>}
    </div>
  );
}

/**
 * `MonthChip` component.
 */

function MonthChip({
  active,
  disabled,
  onClick,
  children
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      className={cn(
        'rounded-md px-2.5 py-1 text-xs font-medium capitalize transition-colors',
        active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent hover:text-muted-foreground'
      )}
      disabled={disabled}
      onClick={onClick}
      type={'button'}
    >
      {children}
    </button>
  );
}

/**
 * `Panel` component.
 */

function Panel({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className={'rounded-xl border border-border bg-card shadow-sm'}>
      <div className={'border-b border-border px-5 py-3.5 text-sm font-semibold text-foreground'}>{title}</div>

      <div className={'p-5'}>{children}</div>
    </div>
  );
}

/**
 * Reshape a CanvasJS-style budget section into Recharts rows + series.
 */

function toBars(section: any, consumedColor: string) {
  const raw: any[] = section?.data ?? [];

  if (!raw.length) {
    return { rows: [] as any[], series: [] as { key: string; label: string; color: string }[] };
  }

  const series = raw.map((item: any, index: number) => ({
    key: `s${index}`,
    label: item.name ?? `Series ${index + 1}`,
    color: index === raw.length - 1 ? consumedColor : BUDGET_COLOR
  }));

  const base: any[] = raw[0]?.dataPoints ?? [];
  const rows = base.map((point: any, index: number) => {
    const row: any = { category: point.label, metadata: point.metadata };

    raw.forEach((item: any, seriesIndex: number) => {
      row[`s${seriesIndex}`] = item.dataPoints?.[index]?.y ?? 0;
    });

    return row;
  });

  return { rows, series };
}

/**
 * Export `MovementsOverview` component.
 */

export function MovementsOverview(): JSX.Element {
  const [translate] = useTranslation();
  const navigate = useNavigate();
  const [monthIndex, setMonthIndex] = useState<number | undefined>();
  const { data, refetch } = useMovementsOverview(monthIndex);

  useEffect(() => {
    if (!monthIndex || monthIndex > -1) {
      refetch();
    }
  }, [monthIndex, refetch]);

  const trend = useMemo(() => {
    const series: any[] = data?.incomeVsExpenses?.data ?? [];

    if (!series.length) {
      return [] as { month: string; income: number; expenses: number }[];
    }

    const income = series.find((item: any) => /income/i.test(item.name))?.dataPoints ?? series[0]?.dataPoints ?? [];
    const expenses = series.find((item: any) => /expense/i.test(item.name))?.dataPoints ?? series[1]?.dataPoints ?? [];

    return income.map((point: any, index: number) => ({
      month: point.label as string,
      income: (point.y ?? 0) as number,
      expenses: (expenses[index]?.y ?? 0) as number
    })) as { month: string; income: number; expenses: number }[];
  }, [data]);

  const monthsWithData = useMemo(
    () =>
      new Set(trend.map((row, index) => (row.income > 0 || row.expenses > 0 ? index : -1)).filter(index => index >= 0)),
    [trend]
  );

  const scope = useMemo(() => {
    if (monthIndex !== undefined && trend[monthIndex]) {
      const row = trend[monthIndex];

      return {
        label: moment().month(monthIndex).format('MMMM'),
        income: new BigNumber(row.income),
        expenses: new BigNumber(row.expenses)
      };
    }

    return {
      label: String(moment().year()),
      income: trend.reduce((acc, row) => acc.plus(row.income), new BigNumber(0)),
      expenses: trend.reduce((acc, row) => acc.plus(row.expenses), new BigNumber(0))
    };
  }, [monthIndex, trend]);

  const net = scope.income.minus(scope.expenses);
  const rate = scope.income.isGreaterThan(0) ? net.dividedBy(scope.income).multipliedBy(100) : new BigNumber(0);

  const budgetSections = useMemo(() => {
    const graph = data?.budgetGraph;

    if (!graph || monthIndex === undefined) {
      return null;
    }

    return {
      monthExpenses: toBars(graph.monthExpenses, chartColors.destructive),
      monthIncomes: toBars(graph.monthIncomes, chartColors.primary),
      yearExpenses: toBars(graph.yearExpenses, chartColors.destructive),
      yearIncomes: toBars(graph.yearIncomes, chartColors.primary)
    };
  }, [data, monthIndex]);

  const onBudgetClick = useCallback(
    (row: any) => {
      const metadata = row?.metadata;

      if (!metadata) {
        return;
      }

      navigate({
        pathname: appRoutes.dashboard.bankAccounts.movementsManager.base,
        search: `?${createSearchParams({
          filter: JSON.stringify({
            orderBy: 'bank_date#desc',
            category: metadata.budget?.subCategoryId,
            year: metadata.yearIndex,
            transactionType: 'normal',
            month: metadata.monthIndex !== undefined ? metadata.monthIndex + 1 : undefined
          }),
          filterOpened: 'true'
        } as any)}`
      });
    },
    [navigate]
  );

  const months = useMemo(() => Array.from({ length: 12 }, (_, index) => moment().month(index).format('MMM')), []);
  const monthLabel = monthIndex !== undefined ? moment().month(monthIndex).format('MMMM') : '';
  const yearLabel = String(moment().year());
  const barHeight = (section: { rows: any[] }) => Math.max(220, section.rows.length * 32 + 64);

  return (
    <div className={'space-y-5'}>
      <div className={'flex flex-wrap items-center justify-between gap-3'}>
        <Title>{translate('bankAccounts.overview.title')}</Title>

        <div className={'flex flex-wrap items-center gap-1'}>
          <MonthChip active={monthIndex === undefined} onClick={() => setMonthIndex(undefined)}>
            {yearLabel}
          </MonthChip>

          {months.map((label, index) => (
            <MonthChip
              active={monthIndex === index}
              disabled={!monthsWithData.has(index)}
              key={label}
              onClick={() => setMonthIndex(index)}
            >
              {label}
            </MonthChip>
          ))}
        </div>
      </div>

      {/* Summary — reflects the selected month, or the whole year */}
      <div className={'grid grid-cols-4 gap-4 max-[991px]:grid-cols-2 max-[600px]:grid-cols-1'}>
        <StatCard
          icon={<TrendingUp />}
          label={translate('common.labels.income')}
          sub={scope.label}
          tone={'text-primary'}
          value={formatCurrency(scope.income)}
        />

        <StatCard
          icon={<TrendingDown />}
          label={translate('common.labels.expenses')}
          sub={scope.label}
          tone={'text-destructive'}
          value={formatCurrency(scope.expenses)}
        />

        <StatCard
          icon={<PiggyBank />}
          label={translate('common.labels.savings')}
          sub={scope.label}
          tone={net.isNegative() ? 'text-destructive' : 'text-primary'}
          value={formatCurrency(net)}
        />

        <StatCard
          icon={<Percent />}
          label={translate('common.labels.savingsPerc')}
          sub={scope.label}
          tone={net.isNegative() ? 'text-destructive' : undefined}
          value={`${rate.toFixed(1)}%`}
        />
      </div>

      {/* Income vs Expenses trend */}
      {trend.length > 0 && (
        <Panel title={translate('bankAccounts.overview.incomeVsExpenses.title')}>
          <AreaTrendChart
            data={trend}
            highlightX={monthIndex !== undefined ? trend[monthIndex]?.month : undefined}
            onPointClick={setMonthIndex}
            series={[
              { key: 'income', label: translate('common.labels.income'), color: chartColors.primary },
              { key: 'expenses', label: translate('common.labels.expenses'), color: chartColors.destructive }
            ]}
            valueFormatter={formatCurrency}
            xKey={'month'}
          />

          <p className={'mt-2 text-center text-xs text-muted-foreground'}>
            {translate('bankAccounts.overview.selectMonthHint')}
          </p>
        </Panel>
      )}

      {/* Budget vs consumed for the selected month */}
      {!!budgetSections && (
        <div className={'grid grid-cols-2 gap-4 max-[1200px]:grid-cols-1'}>
          {(
            [
              {
                key: 'monthExpenses',
                title: translate('bankAccounts.overview.budgets.monthExpenses.title'),
                period: monthLabel
              },
              {
                key: 'monthIncomes',
                title: translate('bankAccounts.overview.budgets.monthIncomes.title'),
                period: monthLabel
              },
              {
                key: 'yearExpenses',
                title: translate('bankAccounts.overview.budgets.yearExpenses.title'),
                period: yearLabel
              },
              {
                key: 'yearIncomes',
                title: translate('bankAccounts.overview.budgets.yearIncomes.title'),
                period: yearLabel
              }
            ] as const
          ).map(section => {
            const bars = (budgetSections as any)[section.key];

            if (!bars?.rows?.length) {
              return null;
            }

            return (
              <Panel key={section.key} title={`${section.title} · ${section.period}`}>
                <CategoryBarChart
                  data={bars.rows}
                  height={barHeight(bars)}
                  layout={'vertical'}
                  onBarClick={onBudgetClick}
                  series={bars.series}
                  valueFormatter={formatCurrency}
                  xKey={'category'}
                />
              </Panel>
            );
          })}
        </div>
      )}
    </div>
  );
}
